import React, { useState, useEffect } from 'react';
import { exportSubscribersToCSV } from '../lib/newsletterService';

// Use the same key as in newsletterService.ts
const SUBSCRIBERS_KEY = 'substans_newsletter_subscribers';

// Simple admin password - in a real app, this would be handled server-side
// This is just a basic protection to prevent casual access
const ADMIN_PASSWORD = '6ba84d7b0d';

interface Subscriber {
  email: string;
  subscribed_at: string;
  source: string;
}

const NewsletterAdmin: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Function to load subscribers data
  const loadSubscribersData = () => {
    setIsLoading(true);
    try {
      // Check if localStorage is available
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      
      const data = localStorage.getItem(SUBSCRIBERS_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        setSubscribers(parsed.subscribers || []);
      } else {
        setSubscribers([]);
      }
    } catch (error) {
      console.error('Error loading subscribers:', error);
      setSubscribers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load subscribers on component mount
  useEffect(() => {
    loadSubscribersData();
  }, []);

  // Handle login attempt
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setErrorMessage('');
    } else {
      setErrorMessage('Invalid password');
    }
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto p-4 sm:p-6 mt-16">
        <h1 className="text-xl sm:text-2xl font-medium mb-4 sm:mb-6 text-center">Newsletter Admin</h1>
        
        <form onSubmit={handleLogin} className="bg-white p-6 rounded-lg shadow-md">
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
              required
            />
          </div>
          
          {errorMessage && (
            <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
          )}
          
          <button
            type="submit"
            className="w-full bg-black text-white py-2 rounded-md hover:bg-gray-800 transition-colors"
          >
            Login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-medium">Newsletter Subscribers</h1>
        <button 
          onClick={() => setIsAuthenticated(false)}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Logout
        </button>
      </div>
      
      <div className="mb-6 flex flex-wrap gap-3 items-start">
        <button 
          onClick={exportSubscribersToCSV}
          className="px-4 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
          disabled={subscribers.length === 0}
        >
          Export as CSV
        </button>
        
        <button 
          onClick={loadSubscribersData}
          className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center gap-1"
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
        
        <p className="text-sm text-gray-500 mt-2 ml-auto">
          Total subscribers: {subscribers.length}
        </p>
      </div>

      {isLoading ? (
        <p>Loading subscribers...</p>
      ) : subscribers.length === 0 ? (
        <p>No subscribers yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="py-2 px-2 sm:px-4 border-b text-left text-xs sm:text-sm">Email</th>
                <th className="py-2 px-2 sm:px-4 border-b text-left text-xs sm:text-sm hidden sm:table-cell">Subscribed At</th>
                <th className="py-2 px-2 sm:px-4 border-b text-left text-xs sm:text-sm hidden sm:table-cell">Source</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((subscriber, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="py-2 px-2 sm:px-4 border-b text-xs sm:text-sm break-all">{subscriber.email}</td>
                  <td className="py-2 px-2 sm:px-4 border-b text-xs sm:text-sm hidden sm:table-cell">
                    {new Date(subscriber.subscribed_at).toLocaleString()}
                  </td>
                  <td className="py-2 px-2 sm:px-4 border-b text-xs sm:text-sm hidden sm:table-cell">{subscriber.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewsletterAdmin;
